import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

/**
 * Campos de formulario.
 *
 * Todo se opera con teclado (D9), así que cada control tiene su etiqueta asociada de
 * verdad —no un texto al lado— y el error se anuncia con `aria-describedby` para que un
 * lector de pantalla lo lea al llegar al campo, no después de enviar.
 */

interface BaseDeCampo {
  readonly etiqueta: string;
  readonly error?: string | undefined;
  readonly ayuda?: string | undefined;
  readonly obligatorio?: boolean | undefined;
}

function Envoltura({
  etiqueta,
  error,
  ayuda,
  obligatorio,
  idDelControl,
  idDeAyuda,
  children,
}: BaseDeCampo & {
  readonly idDelControl: string;
  readonly idDeAyuda: string;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={idDelControl} className="text-xs font-medium text-texto-tenue">
        {etiqueta}
        {obligatorio === true ? <span aria-hidden="true">{' *'}</span> : null}
      </label>
      {children}
      {error === undefined && ayuda === undefined ? null : (
        <p
          id={idDeAyuda}
          className={`text-xs ${error === undefined ? 'text-texto-tenue' : 'text-mal'}`}
        >
          {error ?? ayuda}
        </p>
      )}
    </div>
  );
}

const CLASES_DE_CONTROL =
  'rounded-marco border border-borde bg-superficie px-2 py-1.5 text-sm disabled:opacity-50';

export function Campo({
  etiqueta,
  error,
  ayuda,
  obligatorio,
  ...resto
}: BaseDeCampo &
  InputHTMLAttributes<HTMLInputElement> & {
    /**
     * La referencia al `input` de adentro. En React 19 una función componente recibe `ref` como
     * una prop más, así que alcanza con declararla: viaja con el resto hasta el `input`. Existe
     * para poder poner el foco al abrir un diálogo, que es lo que hace falta cuando quien lo abre
     * viene con un lector de códigos en la mano.
     */
    readonly ref?: React.Ref<HTMLInputElement> | undefined;
  }): React.ReactElement {
  const id = useId();
  const idDeAyuda = `${id}-ayuda`;
  return (
    <Envoltura
      etiqueta={etiqueta}
      error={error}
      ayuda={ayuda}
      obligatorio={obligatorio}
      idDelControl={id}
      idDeAyuda={idDeAyuda}
    >
      <input
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined && ayuda === undefined ? undefined : idDeAyuda}
        className={CLASES_DE_CONTROL}
        {...resto}
      />
    </Envoltura>
  );
}

export function Selector({
  etiqueta,
  error,
  ayuda,
  obligatorio,
  children,
  ...resto
}: BaseDeCampo & SelectHTMLAttributes<HTMLSelectElement>): React.ReactElement {
  const id = useId();
  const idDeAyuda = `${id}-ayuda`;
  return (
    <Envoltura
      etiqueta={etiqueta}
      error={error}
      ayuda={ayuda}
      obligatorio={obligatorio}
      idDelControl={id}
      idDeAyuda={idDeAyuda}
    >
      <select
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined && ayuda === undefined ? undefined : idDeAyuda}
        className={CLASES_DE_CONTROL}
        {...resto}
      >
        {children}
      </select>
    </Envoltura>
  );
}

export function Interruptor({
  etiqueta,
  ayuda,
  ...resto
}: {
  readonly etiqueta: string;
  readonly ayuda?: string | undefined;
} & InputHTMLAttributes<HTMLInputElement>): React.ReactElement {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input id={id} type="checkbox" className="mt-0.5" {...resto} />
      <label htmlFor={id} className="text-sm">
        {etiqueta}
        {ayuda === undefined ? null : (
          <span className="block text-xs text-texto-tenue">{ayuda}</span>
        )}
      </label>
    </div>
  );
}
